import { photoUrl } from './api';
import { compressImage } from './image-compress';
import type { PendingPhoto } from './offline';
import { el, toast } from './ui';

const MAX_FILES = 8;
const MAX_BYTES = 20 * 1024 * 1024;
const ALLOWED = /\.(jpe?g|png|docx?|pptx?|xlsx?|csv|kml|kmz)$/i;

function fileKind(name: string): 'image' | 'file' {
  const ext = name.split('.').pop()?.toLowerCase() || '';
  if (['jpg', 'jpeg', 'png'].includes(ext)) return 'image';
  return 'file';
}

function badge(name: string): string {
  return (name.split('.').pop() || 'FILE').toUpperCase();
}

export function photoField(): {
  element: HTMLElement;
  pending: () => PendingPhoto[];
  setPending: (photos: PendingPhoto[]) => void;
  keptKeys: () => string[];
  setExisting: (keys: string[]) => void;
  dispose: () => void;
} {
  let existing: { key: string; removed: boolean }[] = [];
  let pending: PendingPhoto[] = [];
  const urls: string[] = [];

  const row = el('div', { className: 'photo-row' });
  const input = el('input', {
    type: 'file',
    accept: '.jpg,.jpeg,.png,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.kml,.kmz,image/jpeg,image/png',
    multiple: true,
    className: 'photo-input',
  }) as HTMLInputElement;
  const add = el('button', {
    type: 'button',
    className: 'btn',
    textContent: 'Add files',
  });
  add.addEventListener('click', () => input.click());
  input.addEventListener('change', () => {
    const files = [...(input.files || [])];
    input.value = '';
    void addFiles(files);
  });

  function total(): number {
    return existing.filter((e) => !e.removed).length + pending.length;
  }

  async function addFiles(files: File[]): Promise<void> {
    for (const file of files) {
      if (total() >= MAX_FILES) {
        toast(`Up to ${MAX_FILES} files`, 'info');
        break;
      }
      const image = file.type === 'image/jpeg' || file.type === 'image/png' || /\.(jpe?g|png)$/i.test(file.name);
      if (!ALLOWED.test(file.name) && !image) {
        toast('Use JPG, PNG, Word, PowerPoint, Excel, CSV, KML, or KMZ', 'err');
        continue;
      }
      if (file.size > MAX_BYTES) {
        toast('File is over 20 MB', 'err');
        continue;
      }
      try {
        if (image) {
          const compressed = await compressImage(file);
          pending.push(compressed);
        } else {
          pending.push({
            filename: file.name || 'file',
            contentType: file.type || 'application/octet-stream',
            data: await file.arrayBuffer(),
          });
        }
      } catch (err) {
        toast((err as Error).message || 'Could not add that file', 'err');
      }
    }
    paint();
  }

  function paint(): void {
    for (const url of urls) URL.revokeObjectURL(url);
    urls.length = 0;
    row.replaceChildren();
    for (const item of existing) {
      if (item.removed) continue;
      const cell = el('div', { className: 'photo-cell' });
      const img = el('img', { alt: 'Attached photo', className: 'photo-thumb' });
      cell.append(img);
      void photoUrl(item.key)
        .then((url) => {
          img.src = url;
        })
        .catch(() => {
          img.alt = 'Photo unavailable';
        });
      const remove = el('button', {
        type: 'button',
        className: 'photo-x',
        textContent: '×',
        'aria-label': 'Remove photo',
      });
      remove.addEventListener('click', () => {
        item.removed = true;
        paint();
      });
      cell.append(remove);
      row.append(cell);
    }
    pending.forEach((photo, index) => {
      const url = URL.createObjectURL(new Blob([photo.data], { type: photo.contentType }));
      urls.push(url);
      const cell = el('div', { className: 'photo-cell' });
      if (fileKind(photo.filename) === 'image') {
        cell.append(el('img', { src: url, alt: photo.filename, className: 'photo-thumb' }));
      } else {
        cell.append(
          el('div', { className: 'file-tile' }, [
            el('strong', { textContent: badge(photo.filename) }),
            el('span', { textContent: photo.filename }),
          ])
        );
      }
      const remove = el('button', {
        type: 'button',
        className: 'photo-x',
        textContent: '×',
        'aria-label': 'Remove photo',
      });
      remove.addEventListener('click', () => {
        pending.splice(index, 1);
        paint();
      });
      cell.append(remove);
      row.append(cell);
    });
  }

  const element = el('div', { className: 'block' }, [
    el('span', { className: 'field-label', textContent: 'Photos and files' }),
    add,
    input,
    row,
  ]);

  return {
    element,
    pending: () => pending.map((p) => ({ ...p })),
    setPending: (photos) => {
      pending = photos.map((p) => ({ ...p }));
      paint();
    },
    keptKeys: () => existing.filter((e) => !e.removed).map((e) => e.key),
    setExisting: (keys) => {
      existing = keys.map((key) => ({ key, removed: false }));
      paint();
    },
    dispose: () => {
      for (const url of urls) URL.revokeObjectURL(url);
    },
  };
}

/** Read-only files for a report. */
export function photoGallery(keys: string[]): HTMLElement {
  const row = el('div', { className: 'photo-row' });
  if (!keys.length) return row;
  for (const key of keys) {
    const name = key.split('/').pop() || 'file';
    const kind = fileKind(name);
    if (kind === 'image') {
      const img = el('img', { alt: 'Report photo', className: 'photo-thumb' });
      img.addEventListener('click', () => openLightbox(img.src));
      void photoUrl(key)
        .then((url) => {
          img.src = url;
        })
        .catch(() => {
          img.alt = 'Photo unavailable';
        });
      row.append(img);
    } else {
      const tile = el('button', {
        type: 'button',
        className: 'file-tile',
      });
      tile.append(el('strong', { textContent: badge(name) }), el('span', { textContent: name }));
      tile.addEventListener('click', () => {
        void photoUrl(key).then((url) => {
          const a = document.createElement('a');
          a.href = url;
          a.target = '_blank';
          if (kind === 'file') a.download = name;
          a.click();
        });
      });
      row.append(tile);
    }
  }
  return row;
}

function openLightbox(src: string): void {
  if (!src) return;
  const img = el('img', { src, alt: 'Photo', className: 'lightbox-img' });
  const close = el('button', {
    type: 'button',
    className: 'btn btn-primary',
    textContent: 'Close',
  });
  const box = el('div', { className: 'lightbox' }, [img, close]);
  const dismiss = () => box.remove();
  close.addEventListener('click', dismiss);
  box.addEventListener('click', (event) => {
    if (event.target === box) dismiss();
  });
  document.body.append(box);
}
